/**
 * Blog-Article Detail-Page — analog HeyGen-Studio Detail.
 *
 * Tabs:
 *   • Overview     — status, archetype, timeline, links
 *   • Research     — Perplexity-Snapshot + Citations
 *   • Sections     — TOC mit per-section Draft + Reviewer-Notes
 *   • Compliance   — Pass-C-Violations (extracted claim → no_match)
 *   • Body         — gerenderter Polished Markdown mit atlas://-Platzhaltern
 *   • Images       — Evidence-Blocks (DAM/Statamic/Generated)
 *
 * Actions:
 *   • Approve+Publish  — bei needs_human_review/compliance_blocked
 *   • Republish        — bei approved/generated/published
 *   • Archive          — hide aus Pipeline
 *
 * Live-Auto-Refresh alle 5s solange status in-flight.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowLeft,
  CheckCircle2,
  AlertOctagon,
  RotateCw,
  Loader2,
  Archive,
  ExternalLink,
  Globe,
  FileText,
  ShieldAlert,
  Image as ImageIcon,
  Microscope,
  ScrollText,
  Send,
  Clock,
  Layers,
} from "lucide-react";
import { api } from "../api";
import { Button, StatusBadge, statusLabel, imageRoleLabel } from "../ui";
import { useToast } from "../ui/toast";
import { useModal } from "../ui/modal";

interface ArticleOut {
  id: string;
  project_id: string;
  keyword_seed_id: string | null;
  pillar: string;
  axis: string;
  content_type: string | null;
  primary_keyword: string;
  archetype_key: string | null;
  language: string;
  title: string | null;
  slug: string | null;
  body_md: string | null;
  meta_description: string | null;
  word_count: number | null;
  status: string;
  autopilot: boolean;
  reviewer_scores: Record<string, { score: number; must_fix: string[]; nice_to_fix: string[] }> | null;
  compliance_violations: Array<{ extracted_claim: string; no_match?: boolean }> | null;
  research_json: {
    query?: string;
    content?: string;
    citations?: Array<{ index?: number; url?: string; title?: string | null }>;
    mode?: string;
    model?: string;
    tokens_in?: number;
    tokens_out?: number;
  } | null;
  research_mode: string | null;
  statamic_collection: string | null;
  statamic_slug: string | null;
  published_url: string | null;
  needs_review_task_id: string | null;
  created_at: string;
  updated_at: string;
  approved_at: string | null;
  published_at: string | null;
}

interface SectionOut {
  id: string;
  article_id: string;
  section_order: number;
  section_role: string;
  heading: string;
  body_md: string | null;
  attached_claim_ids: string[];
  target_words: number | null;
  voice_modulation: Record<string, unknown>;
  status: string;
  reviewer_score: number | null;
  reviewer_notes: Record<string, unknown> | null;
  regenerate_count: number;
  generated_at: string | null;
}

interface EvidenceOut {
  id: string;
  article_id: string;
  section_id: string | null;
  role: string;
  source_hash: string | null;
  entity_type: string | null;
  sub_id: string | null;
  dam_asset_id: string | null;
  marketing_asset_id: string | null;
  image_url: string | null;
  image_alt: string | null;
  image_generated_by: string | null;
  image_prompt: string | null;
  display_order: number;
  used_in_final: boolean;
  notes: string | null;
}

interface ArticleDetail {
  article: ArticleOut;
  sections: SectionOut[];
  evidence: EvidenceOut[];
}

// S1: Skeleton-Stage output (loaded separately from /skeleton endpoint)
interface SkeletonEvidenceNote {
  source?: string;
  ref_id?: string;
  note?: string;
  use_for_brief_item?: string;
}

interface SkeletonSection {
  section_order: number;
  h2?: string;
  direct_answer?: string;
  word_target?: number;
  keyword_targets?: { primary?: string[]; secondary?: string[] };
  content_brief?: string[];
  do_not_repeat?: string[];
  evidence_notes?: SkeletonEvidenceNote[];
  format_hints?: string[];
  hero_paragraph?: string | null;
  safety_note?: string;
}

interface SkeletonDifferentiationElement {
  type?: string;
  placement_h2?: string;
  description?: string;
  writer_instruction?: string;
}

interface SkeletonFaqEntry {
  question?: string;
  answer_brief?: string;
  differentiation_note?: string;
}

interface SkeletonJson {
  solution_role?: string;
  hero_section_index?: number;
  early_positioning?: boolean;
  sections?: SkeletonSection[];
  differentiation_elements?: SkeletonDifferentiationElement[];
  faq_block?: SkeletonFaqEntry[];
  writing_guidelines?: Record<string, unknown>;
  _parse_error?: string;
  _raw_head?: string;
}

interface RagHit {
  text?: string;
  similarity?: number;
  source?: string | null;
}

interface SkeletonOut {
  id: string;
  article_id: string;
  skeleton_json: SkeletonJson;
  rag_hits_json: Record<string, RagHit[]>;
  llm_model: string | null;
  tokens_in: number | null;
  tokens_out: number | null;
  cost_eur: number | null;
  created_at: string;
}

type Tab =
  | "overview"
  | "research"
  | "skeleton"
  | "sections"
  | "compliance"
  | "body"
  | "images";

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: "overview", label: "Übersicht", icon: <Microscope className="w-3.5 h-3.5" /> },
  { id: "research", label: "Recherche", icon: <Globe className="w-3.5 h-3.5" /> },
  { id: "skeleton", label: "Struktur", icon: <Layers className="w-3.5 h-3.5" /> },
  { id: "sections", label: "Abschnitte", icon: <ScrollText className="w-3.5 h-3.5" /> },
  { id: "compliance", label: "Compliance", icon: <ShieldAlert className="w-3.5 h-3.5" /> },
  { id: "body", label: "Text", icon: <FileText className="w-3.5 h-3.5" /> },
  { id: "images", label: "Bilder", icon: <ImageIcon className="w-3.5 h-3.5" /> },
];

const IN_FLIGHT = new Set([
  "queued",
  "researching",
  "toc_building",
  "toc_approved",
  "skeleton_building",
  "generating",
  "publishing",
]);

export default function BlogArticleDetailPage() {
  const { articleId } = useParams<{ articleId: string }>();
  const toast = useToast();
  const modal = useModal();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<ArticleDetail | null>(null);
  const [skeleton, setSkeleton] = useState<SkeletonOut | null>(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<Tab>("overview");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!articleId) return;
    setLoading(true);
    try {
      const data = await api.get<ArticleDetail>(`/api/blog/articles/${articleId}`);
      setDetail(data);
    } catch (e: unknown) {
      toast.err("Article laden fehlgeschlagen", String(e));
    } finally {
      setLoading(false);
    }
    // Skeleton is its own endpoint — silently 'null' for v2.3 articles
    // that never went through _phase_skeleton. Loaded once on mount and
    // again with each refresh tick.
    try {
      const sk = await api.get<SkeletonOut | null>(
        `/api/blog/articles/${articleId}/skeleton`,
      );
      setSkeleton(sk);
    } catch {
      setSkeleton(null);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId]);

  // Live-Refresh waehrend Pipeline laeuft
  useEffect(() => {
    if (!detail) return;
    if (!IN_FLIGHT.has(detail.article.status)) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);

  const approve = async () => {
    if (!detail) return;
    const ok = await modal.confirm({
      title: "Approve + publishable machen?",
      description:
        "Article wird auf status=approved gesetzt. Compliance-Block ist damit manuell ueberbrueckt. Danach kannst Du republish triggern.",
      confirmLabel: "Approve",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/api/blog/articles/${detail.article.id}/approve`);
      toast.ok("Approved.");
      await load();
    } catch (e: unknown) {
      toast.err("Approve fehlgeschlagen", String(e));
    } finally {
      setBusy(false);
    }
  };

  const republish = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      await api.post(`/api/blog/articles/${detail.article.id}/republish`);
      toast.ok("Republish enqueued.");
      await load();
    } catch (e: unknown) {
      toast.err("Republish fehlgeschlagen", String(e));
    } finally {
      setBusy(false);
    }
  };

  const archive = async () => {
    if (!detail) return;
    const ok = await modal.confirm({
      title: "Article archivieren?",
      description: "Wird aus Pipeline-View versteckt, bleibt in DB.",
      confirmLabel: "Archive",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/api/blog/articles/${detail.article.id}/archive`);
      toast.ok("Archived.");
      navigate("/blog");
    } catch (e: unknown) {
      toast.err("Archive fehlgeschlagen", String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!articleId) return <div className="p-6">Kein articleId.</div>;
  if (loading && !detail) {
    return (
      <div className="p-6 flex items-center gap-2 text-text-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Lade Artikel ...
      </div>
    );
  }
  if (!detail) return <div className="p-6">Artikel nicht gefunden.</div>;

  const a = detail.article;
  const canApprove =
    a.status === "needs_human_review" ||
    a.status === "compliance_blocked" ||
    a.status === "reviewer_blocked" ||
    a.status === "generated";
  const canRepublish =
    a.status === "approved" || a.status === "generated" || a.status === "published";
  const canArchive = a.status !== "archived";

  return (
    <div className="px-4 md:px-6 py-4 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3 text-xs">
        <Link to="/blog" className="text-text-2 hover:text-brand-600 inline-flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> Pipeline
        </Link>
        <span className="text-text-3">/</span>
        <span className="text-text-2">{a.pillar} · {a.axis}</span>
      </div>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h1 className="text-xl md:text-2xl font-semibold leading-tight">
            {a.title || a.primary_keyword}
          </h1>
          <div className="text-xs text-text-2 mt-1">
            archetype:{" "}
            <span className="font-medium text-text">{a.archetype_key || "—"}</span>
            {" · "}
            Recherche:{" "}
            <span className="font-medium text-text">
              {a.research_mode && a.research_mode !== "none" ? "extern" : "keine"}
            </span>
            {a.word_count != null && (
              <>
                {" · "}
                <span className="font-medium text-text">{a.word_count} Wörter</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={statusLabel(a.status).status}>
            {statusLabel(a.status).label}
          </StatusBadge>
          <button
            onClick={load}
            disabled={loading}
            className="px-2 py-1.5 rounded-md border border-border-strong hover:bg-surface-3 min-h-11 min-w-11 md:min-h-0 md:min-w-0 flex items-center justify-center"
            title="Neu laden"
            aria-label="Article neu laden"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
          </button>
          {canApprove && (
            <Button
              onClick={approve}
              disabled={busy}
              icon={<CheckCircle2 className="w-4 h-4" />}
              className="min-h-11 md:min-h-0"
            >
              Freigeben
            </Button>
          )}
          {canRepublish && (
            <Button
              onClick={republish}
              disabled={busy}
              icon={<Send className="w-4 h-4" />}
              className="min-h-11 md:min-h-0"
            >
              Erneut veröffentlichen
            </Button>
          )}
          {canArchive && (
            <Button
              variant="danger"
              onClick={archive}
              disabled={busy}
              icon={<Archive className="w-4 h-4" />}
              className="min-h-11 md:min-h-0"
            >
              Archivieren
            </Button>
          )}
        </div>
      </div>

      {/* Quick-Banner: needs_human_review */}
      {a.status === "needs_human_review" && (
        <div className="border border-fuchsia-200 bg-fuchsia-50 rounded-md p-3 mb-4 text-sm flex items-start gap-2">
          <AlertOctagon className="w-4 h-4 text-fuchsia-600 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="font-medium text-fuchsia-800">
              Pipeline blockiert — manuelle Pruefung notwendig
            </div>
            <div className="text-xs text-fuchsia-700/80 mt-0.5">
              {a.compliance_violations && a.compliance_violations.length > 0
                ? `${a.compliance_violations.length} compliance violations — siehe Tab Compliance.`
                : "Reviewer-Score < 7 — siehe Tab Sections / reviewer_scores."}
              {a.needs_review_task_id && (
                <>
                  {" · "}
                  <Link
                    to={`/tasks?view=kanban&task=${a.needs_review_task_id}`}
                    className="underline"
                  >
                    Atlas-Task oeffnen
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-border mb-4 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-1.5 ${
                tab === t.id
                  ? "border-brand-500 text-brand-700"
                  : "border-transparent text-text-2 hover:text-text"
              }`}
            >
              {t.icon} {t.label}
              {t.id === "sections" && detail.sections.length > 0 && (
                <span className="text-[10px] text-text-3">({detail.sections.length})</span>
              )}
              {t.id === "compliance" && a.compliance_violations && a.compliance_violations.length > 0 && (
                <span className="text-[10px] bg-danger-soft text-danger px-1 rounded-md">
                  {a.compliance_violations.length}
                </span>
              )}
              {t.id === "images" && detail.evidence.length > 0 && (
                <span className="text-[10px] text-text-3">({detail.evidence.length})</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {tab === "overview" && <OverviewTab detail={detail} />}
      {tab === "research" && <ResearchTab detail={detail} />}
      {tab === "skeleton" && <SkeletonTab skeleton={skeleton} />}
      {tab === "sections" && <SectionsTab detail={detail} />}
      {tab === "compliance" && <ComplianceTab detail={detail} />}
      {tab === "body" && <BodyTab detail={detail} />}
      {tab === "images" && <ImagesTab detail={detail} />}
    </div>
  );
}

// ----------------- Tabs -----------------

function OverviewTab({ detail }: { detail: ArticleDetail }) {
  const a = detail.article;
  const polish = a.reviewer_scores?.polish;
  const phases: { label: string; at: string | null; ok: boolean | null }[] = [
    { label: "In Warteschlange", at: a.created_at, ok: true },
    { label: "Recherche", at: a.research_json ? a.updated_at : null, ok: !!a.research_json },
    { label: "Abschnitte", at: detail.sections.length > 0 ? a.updated_at : null, ok: detail.sections.length > 0 },
    { label: "Feinschliff", at: a.body_md ? a.updated_at : null, ok: !!a.body_md },
    { label: "Prüfung", at: polish ? a.updated_at : null, ok: polish ? polish.score >= 7 : null },
    { label: "Compliance", at: a.compliance_violations !== null ? a.updated_at : null, ok: (a.compliance_violations || []).length === 0 ? true : false },
    { label: "Freigegeben", at: a.approved_at, ok: !!a.approved_at },
    { label: "Veröffentlicht", at: a.published_at, ok: !!a.published_at },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="border border-border rounded-lg p-4 bg-surface">
        <h3 className="text-xs uppercase tracking-wide font-medium text-text-2 mb-3">
          Metadaten
        </h3>
        <dl className="text-sm space-y-2">
          <Row label="Article-ID" value={<code className="text-[11px]">{a.id}</code>} />
          <Row label="Seed-ID" value={<code className="text-[11px]">{a.keyword_seed_id || "—"}</code>} />
          <Row label="Primary-Keyword" value={a.primary_keyword} />
          <Row label="Pillar / Axis" value={`${a.pillar} · ${a.axis}`} />
          <Row label="Content-Type" value={a.content_type || "—"} />
          <Row label="Archetype" value={a.archetype_key || "—"} />
          <Row label="Language" value={a.language} />
          <Row label="Word-Count" value={a.word_count ?? "—"} />
          <Row label="Slug" value={a.slug ? <code className="text-[11px]">{a.slug}</code> : "—"} />
          <Row
            label="Statamic"
            value={
              a.published_url ? (
                <a
                  href={a.published_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-600 hover:underline inline-flex items-center gap-1"
                >
                  öffnen <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                `${a.statamic_collection || "blog"} / ${a.statamic_slug || "—"}`
              )
            }
          />
        </dl>
      </div>

      <div className="border border-border rounded-lg p-4 bg-surface">
        <h3 className="text-xs uppercase tracking-wide font-medium text-text-2 mb-3">
          Pipeline-Timeline
        </h3>
        <ol className="space-y-1.5">
          {phases.map((p) => (
            <li key={p.label} className="flex items-center gap-2 text-sm">
              <span
                className={`w-2 h-2 rounded-full shrink-0 ${
                  p.ok === true
                    ? "bg-success"
                    : p.ok === false
                    ? "bg-danger"
                    : "bg-surface-3"
                }`}
              />
              <span className="w-32 shrink-0 text-text font-medium">{p.label}</span>
              <Clock className="w-3 h-3 text-text-3" />
              <span className="text-xs text-text-2 tabular-nums">
                {/* Locale FEST auf de-DE — ohne Argument formatiert der Browser
                    in seiner eigenen Sprache (englisches Chrome: US-Datum). */}
                {p.at ? new Date(p.at).toLocaleString("de-DE") : "—"}
              </span>
            </li>
          ))}
        </ol>

        {polish && (
          <div className="mt-4 pt-3 border-t border-border">
            <h4 className="text-xs uppercase tracking-wide font-medium text-text-2 mb-1">
              Prüfung (Feinschliff)
            </h4>
            <div className="text-2xl font-semibold tabular-nums">
              {polish.score.toFixed(1)} <span className="text-sm text-text-3">/ 10</span>
            </div>
            {polish.must_fix.length > 0 && (
              <ul className="text-xs text-danger mt-1 list-disc list-inside space-y-0.5">
                {polish.must_fix.slice(0, 5).map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            )}
            {polish.nice_to_fix.length > 0 && (
              <ul className="text-xs text-text-2 mt-1 list-disc list-inside space-y-0.5">
                {polish.nice_to_fix.slice(0, 3).map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <dt className="text-text-2 shrink-0">{label}</dt>
      <dd className="text-right break-all">{value}</dd>
    </div>
  );
}

function ResearchTab({ detail }: { detail: ArticleDetail }) {
  const r = detail.article.research_json;
  if (!r) {
    return (
      <div className="text-sm text-text-2 italic">
        Noch keine Research. Wird beim ersten Pipeline-Run angelegt.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
      <div className="border border-border rounded-lg p-4 bg-surface">
        <h3 className="text-xs uppercase tracking-wide font-medium text-text-2 mb-2">
          Research-Snapshot ({r.mode || "?"} · {r.model || "?"})
        </h3>
        {r.query && (
          <details className="text-xs text-text-2 mb-3">
            <summary className="cursor-pointer hover:text-text">
              Query anzeigen ({r.query.length} chars)
            </summary>
            <pre className="mt-2 whitespace-pre-wrap text-[11px] font-mono bg-surface-2 p-2 rounded-md">
              {r.query}
            </pre>
          </details>
        )}
        <div className="prose prose-sm max-w-none text-text">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {r.content || "(leer)"}
          </ReactMarkdown>
        </div>
      </div>
      <div className="border border-border rounded-lg p-4 bg-surface">
        <h3 className="text-xs uppercase tracking-wide font-medium text-text-2 mb-2">
          Quellen ({(r.citations || []).length})
        </h3>
        <ol className="text-xs space-y-1.5">
          {(r.citations || []).map((c, i) => (
            <li key={i} className="text-text">
              <span className="font-mono text-text-3 mr-1">[{c.index ?? i + 1}]</span>
              {c.url ? (
                <a
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-600 hover:underline"
                >
                  {c.title || c.url}
                </a>
              ) : (
                c.title || "—"
              )}
            </li>
          ))}
          {!r.citations?.length && (
            <li className="text-text-3 italic">keine Quellen.</li>
          )}
        </ol>
        <div className="mt-3 pt-2 border-t border-border text-[11px] text-text-2">
          Tokens: in={r.tokens_in ?? 0} · out={r.tokens_out ?? 0}
        </div>
      </div>
    </div>
  );
}

function SectionsTab({ detail }: { detail: ArticleDetail }) {
  if (detail.sections.length === 0) {
    return (
      <div className="text-sm text-text-2 italic">
        Noch keine Sections. Werden in toc_building-Phase angelegt.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {detail.sections.map((s) => (
        <details
          key={s.id}
          className="border border-border rounded-lg bg-surface open:shadow-sm"
        >
          <summary className="px-3 py-2 cursor-pointer hover:bg-surface-2 flex items-center gap-2">
            <span className="text-xs font-mono text-text-3 w-6 text-right">
              {s.section_order}
            </span>
            <span className="text-xs uppercase tracking-wide text-text-2 w-32 truncate">
              {s.section_role}
            </span>
            <span className="text-sm font-medium text-text flex-1 truncate">
              {s.heading}
            </span>
            <StatusBadge status={statusLabel(s.status).status} className="text-[10px]">
              {statusLabel(s.status).label}
            </StatusBadge>
            <span className="text-[10px] text-text-2 w-16 text-right tabular-nums">
              {(s.body_md || "").length} chars
            </span>
            {s.attached_claim_ids.length > 0 && (
              <span className="text-[10px] text-text-2">
                {s.attached_claim_ids.length} claims
              </span>
            )}
            {s.regenerate_count > 0 && (
              <span className="text-[10px] text-warning">
                regen {s.regenerate_count}
              </span>
            )}
          </summary>
          <div className="px-4 py-3 border-t border-border">
            {s.body_md ? (
              <div className="prose prose-sm max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{s.body_md}</ReactMarkdown>
              </div>
            ) : (
              <div className="text-sm text-text-3 italic">(noch kein Text)</div>
            )}
            {s.reviewer_notes && Object.keys(s.reviewer_notes).length > 0 && (
              <div className="mt-3 pt-3 border-t border-border">
                <div className="text-xs uppercase tracking-wide text-text-2 mb-1">
                  Reviewer-Notes
                </div>
                <pre className="text-[11px] bg-surface-2 p-2 rounded-md whitespace-pre-wrap">
                  {JSON.stringify(s.reviewer_notes, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </details>
      ))}
    </div>
  );
}

function ComplianceTab({ detail }: { detail: ArticleDetail }) {
  const v = detail.article.compliance_violations || [];
  return (
    <div className="space-y-3">
      <div className="text-sm text-text">
        Compliance-Gate (Pass C, deterministisch): extrahierte Wirk-Claims werden
        gegen <code>cosmetic_claim_proof_matrix WHERE status='approved'</code>{" "}
        substring/token-overlap-matched (v1 — Sprint 1.5 = embedding-cosine).
      </div>
      {v.length === 0 ? (
        <div className="border border-success bg-success-soft rounded-md p-3 text-sm text-success inline-flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> Keine Verstöße. Artikel ist veröffentlichungsbereit.
        </div>
      ) : (
        <div className="border border-danger bg-danger-soft rounded-md">
          <div className="px-3 py-2 text-sm font-medium text-danger border-b border-danger">
            {v.length} Verstöße
          </div>
          <ol className="divide-y divide-danger/20">
            {v.map((vi, i) => (
              <li key={i} className="px-3 py-2 text-sm">
                <div className="flex items-start gap-2">
                  <span className="text-[10px] font-mono text-danger w-6 text-right pt-1">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-text">{vi.extracted_claim}</div>
                    <div className="text-[10px] text-danger mt-0.5">
                      → no_match in claim_proof_matrix
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

// Entfernt das kanonische Bild-Alt-Präfix für eine saubere Caption.
function cleanCaption(alt: string | null | undefined): string {
  return String(alt || "")
    .replace(/^(Editorial hero image|Data visuali[sz]ation|Illustration|Image)\s*:\s*/i, "")
    .trim();
}

// Artikel-taugliche Markdown-Komponenten (das @tailwindcss/typography-Plugin
// ist NICHT installiert → `prose` ist ein No-Op; wir stylen H1/H2/Listen/Tabellen
// hier explizit, damit die Struktur sichtbar ist). Bilder werden als
// größenbegrenzte, zentrierte <figure> gerendert (Stat-Grafik im Fließtext
// eingebettet statt vollflächig).
const ARTICLE_MD_COMPONENTS = {
  h1: ({ node, ...props }: any) => (
    <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-ink mt-8 mb-3 first:mt-0" {...props} />
  ),
  h2: ({ node, ...props }: any) => (
    <h2 className="font-display text-xl md:text-2xl font-semibold tracking-tight text-ink mt-8 mb-3 pb-1.5 border-b border-cream-200 dark:border-ink-600 first:mt-0" {...props} />
  ),
  h3: ({ node, ...props }: any) => (
    <h3 className="text-base md:text-lg font-semibold text-ink mt-6 mb-2" {...props} />
  ),
  h4: ({ node, ...props }: any) => (
    <h4 className="text-xs font-semibold uppercase tracking-wide text-text-2 mt-5 mb-2" {...props} />
  ),
  p: ({ node, ...props }: any) => (
    <p className="text-[15px] leading-7 text-ink/90 my-3" {...props} />
  ),
  ul: ({ node, ...props }: any) => (
    <ul className="list-disc pl-6 my-3 space-y-1.5 text-[15px] leading-7 text-ink/90" {...props} />
  ),
  ol: ({ node, ...props }: any) => (
    <ol className="list-decimal pl-6 my-3 space-y-1.5 text-[15px] leading-7 text-ink/90" {...props} />
  ),
  li: ({ node, ...props }: any) => <li className="pl-1 marker:text-text-3" {...props} />,
  a: ({ node, ...props }: any) => (
    <a className="text-brand-600 hover:underline" target="_blank" rel="noopener noreferrer" {...props} />
  ),
  strong: ({ node, ...props }: any) => (
    <strong className="font-semibold text-ink" {...props} />
  ),
  em: ({ node, ...props }: any) => <em className="italic" {...props} />,
  blockquote: ({ node, ...props }: any) => (
    <blockquote className="border-l-4 border-brand-200 dark:border-brand-700 bg-cream-50 dark:bg-ink-700/40 pl-4 pr-3 py-2 my-4 rounded-r text-ink/80 [&>p]:my-1" {...props} />
  ),
  hr: () => <hr className="my-6 border-cream-200 dark:border-ink-600" />,
  code: ({ node, ...props }: any) => (
    <code className="text-[13px] bg-cream-100 dark:bg-ink-700 px-1.5 py-0.5 rounded-md" {...props} />
  ),
  table: ({ node, ...props }: any) => (
    <div className="my-4 overflow-x-auto">
      <table className="w-full text-sm border-collapse" {...props} />
    </div>
  ),
  thead: ({ node, ...props }: any) => (
    <thead className="bg-cream-100 dark:bg-ink-700" {...props} />
  ),
  th: ({ node, ...props }: any) => (
    <th className="border border-cream-200 dark:border-ink-600 px-3 py-2 text-left font-semibold" {...props} />
  ),
  td: ({ node, ...props }: any) => (
    <td className="border border-cream-200 dark:border-ink-600 px-3 py-2 align-top" {...props} />
  ),
  img: ({ node, src, alt, title, ...props }: any) => {
    const altStr = String(alt || "");
    const isHero = /^Editorial hero image:/i.test(altStr);
    const isStat = /^Data visuali[sz]ation:/i.test(altStr);
    // Chart-Provenienz kommt als Markdown-Image-Title (→ <figcaption>); sonst
    // aus dem Alt-Text.
    const titleCap = typeof title === "string" ? title.trim() : "";
    const caption = titleCap || cleanCaption(altStr);
    if (isHero) {
      // Lead-Bild: breit, aber höhenbegrenzt (kein Vollbild-Slop).
      return (
        <img
          src={src}
          alt={caption}
          loading="lazy"
          className="w-full max-h-72 object-cover rounded-lg my-5 border border-cream-200 dark:border-ink-600"
          {...props}
        />
      );
    }
    // Stat-Grafik / Mechanismus-Illustration: im Fließtext eingebettet,
    // größenbegrenzt und zentriert — NICHT vollflächig.
    return (
      <figure className={`my-5 mx-auto ${isStat ? "max-w-[18rem]" : "max-w-md"}`}>
        <img
          src={src}
          alt={caption}
          loading="lazy"
          className="w-full rounded-lg border border-cream-200 dark:border-ink-600"
          {...props}
        />
        {caption && !isStat && (
          <figcaption className="mt-1.5 text-xs text-text-2 text-center italic">
            {caption}
          </figcaption>
        )}
      </figure>
    );
  },
};

function BodyTab({ detail }: { detail: ArticleDetail }) {
  const a = detail.article;
  // Highlight atlas://-Platzhalter durch ein lightbox-aehnliches Markup.
  const md = useMemo(() => {
    if (!a.body_md) return null;
    let out = a.body_md.replace(
      /\(atlas:\/\/([^)]+)\)/g,
      (_m, id) => `(#atlas-${id} "atlas-Platzhalter — bei Publish wird Bild verlinkt")`,
    );
    // Hero-Markierung: das ERSTE Bild VOR der ersten H2 ist das Lead-Bild
    // (anchor_kind=before_first_h2). LLM-generierte Alts tragen KEIN
    // "Editorial hero image:"-Präfix — hier deterministisch ergänzen, damit
    // der img-Renderer es breit (statt eingebettet) darstellt.
    const knownPrefix = /^(Editorial hero image|Data visuali[sz]ation|Illustration|Image)\s*:/i;
    const h2Idx = out.search(/^##\s/m);
    let firstSeen = false;
    out = out.replace(/!\[([^\]]*)\]\(/g, (full, alt, offset: number) => {
      if (firstSeen) return full;
      firstSeen = true;
      const beforeFirstH2 = h2Idx === -1 || offset < h2Idx;
      if (beforeFirstH2 && !knownPrefix.test(alt)) {
        return `![Editorial hero image: ${alt}](`;
      }
      return full;
    });
    return out;
  }, [a.body_md]);
  if (!md) {
    return (
      <div className="text-sm text-text-2 italic">
        Noch kein body_md. Wird nach Pass-B-Polish gesetzt.
      </div>
    );
  }
  return (
    <div className="border border-cream-200 dark:border-ink-600 rounded-lg px-5 md:px-8 py-6 bg-surface dark:bg-ink-800">
      <div className="max-w-3xl mx-auto">
        {a.title && (
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-ink mb-1">
            {a.title}
          </h1>
        )}
        {a.meta_description && (
          <div className="text-sm text-text-2 italic mb-5 pb-4 border-b border-cream-200 dark:border-ink-600">
            {a.meta_description}
          </div>
        )}
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={ARTICLE_MD_COMPONENTS}>
          {md}
        </ReactMarkdown>
      </div>
    </div>
  );
}

function SkeletonTab({ skeleton }: { skeleton: SkeletonOut | null }) {
  if (!skeleton) {
    return (
      <div className="text-sm text-text-2 italic">
        Noch kein Skeleton. Wird in der skeleton_building-Phase angelegt (S1
        Migration 0038). Altere Artikel ohne Skeleton-Stage bleiben leer.
      </div>
    );
  }
  const s = skeleton.skeleton_json || {};
  const hasParseError = Boolean(s._parse_error);
  const sections = s.sections || [];
  const ragByOrder = skeleton.rag_hits_json || {};
  const totalRagHits = Object.values(ragByOrder).reduce(
    (acc, hits) => acc + (hits?.length || 0),
    0,
  );

  return (
    <div className="space-y-4">
      {/* Meta-Bar */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-text-2 border border-border rounded-lg px-3 py-2 bg-surface">
        <span>
          <span className="text-text-3">solution_role:</span>{" "}
          <span className="font-medium text-text">
            {s.solution_role || "—"}
          </span>
        </span>
        <span>
          <span className="text-text-3">hero_section:</span>{" "}
          <span className="font-medium text-text">
            {typeof s.hero_section_index === "number"
              ? s.hero_section_index === -1
                ? "none"
                : `#${s.hero_section_index}`
              : "—"}
          </span>
        </span>
        <span>
          <span className="text-text-3">sections:</span>{" "}
          <span className="font-medium text-text">{sections.length}</span>
        </span>
        <span>
          <span className="text-text-3">rag_hits:</span>{" "}
          <span className="font-medium text-text">{totalRagHits}</span>
        </span>
        {skeleton.cost_eur != null && (
          <span>
            <span className="text-text-3">cost:</span>{" "}
            <span className="font-medium text-text tabular-nums">
              EUR {Number(skeleton.cost_eur).toFixed(4)}
            </span>
          </span>
        )}
        {(skeleton.tokens_in != null || skeleton.tokens_out != null) && (
          <span>
            <span className="text-text-3">tokens:</span>{" "}
            <span className="font-medium text-text tabular-nums">
              {skeleton.tokens_in ?? 0} / {skeleton.tokens_out ?? 0}
            </span>
          </span>
        )}
      </div>

      {/* Parse-Error Banner */}
      {hasParseError && (
        <div className="border border-danger bg-danger-soft rounded-md p-3 text-sm">
          <div className="font-medium text-danger inline-flex items-center gap-2">
            <AlertOctagon className="w-4 h-4" /> Skeleton-JSON konnte nicht geparst werden
          </div>
          <div className="text-xs text-danger mt-1">{s._parse_error}</div>
          {s._raw_head && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-danger">
                Rohdaten-Anfang ({s._raw_head.length} Zeichen)
              </summary>
              <pre className="mt-2 whitespace-pre-wrap font-mono bg-surface p-2 rounded-md border border-danger">
                {s._raw_head}
              </pre>
            </details>
          )}
        </div>
      )}

      {/* Differentiation Elements */}
      {s.differentiation_elements && s.differentiation_elements.length > 0 && (
        <div className="border border-accent-200 rounded-lg p-3 bg-accent-50/40">
          <div className="text-xs uppercase tracking-wide font-medium text-accent-700 mb-2">
            Differenzierungs-Elemente
          </div>
          <ul className="space-y-2">
            {s.differentiation_elements.map((el, i) => (
              <li key={i} className="text-sm">
                <span className="inline-block text-[10px] font-mono uppercase tracking-wide bg-accent-100 text-accent-800 px-1.5 py-0.5 rounded-md mr-2">
                  {el.type || "—"}
                </span>
                <span className="text-text">
                  {el.placement_h2 ? <span className="font-medium">in &ldquo;{el.placement_h2}&rdquo;: </span> : null}
                  {el.description || el.writer_instruction || "—"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Section Cards */}
      {sections.length === 0 ? (
        <div className="text-sm text-text-2 italic">
          Skeleton enthaelt keine Sections (entweder leer oder Parse-Fehler).
        </div>
      ) : (
        <div className="space-y-3">
          {sections.map((sec, idx) => {
            const ragHits = ragByOrder[String(sec.section_order)] || [];
            return (
              <div
                key={`${sec.section_order}-${idx}`}
                className="border border-border rounded-lg bg-surface"
              >
                {/* Header */}
                <div className="px-4 py-2.5 border-b border-border flex items-start gap-3">
                  <span className="text-xs font-mono text-text-3 w-6 text-right pt-0.5">
                    {sec.section_order}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-text">
                      {sec.h2 || "(no H2)"}
                    </div>
                    {typeof sec.word_target === "number" && (
                      <div className="text-[11px] text-text-2 mt-0.5">
                        word_target {sec.word_target}
                      </div>
                    )}
                  </div>
                </div>

                <div className="px-4 py-3 space-y-3 text-sm">
                  {/* Direct Answer */}
                  {sec.direct_answer && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide font-medium text-success mb-1">
                        Direkte Antwort
                      </div>
                      <div className="text-text leading-relaxed border-l-2 border-success pl-3">
                        {sec.direct_answer}
                      </div>
                    </div>
                  )}

                  {/* Content Brief */}
                  {sec.content_brief && sec.content_brief.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide font-medium text-text-2 mb-1">
                        Inhalts-Briefing
                      </div>
                      <ul className="list-disc list-inside space-y-0.5 text-text">
                        {sec.content_brief.map((b, i) => (
                          <li key={i}>{b}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Do Not Repeat */}
                  {sec.do_not_repeat && sec.do_not_repeat.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide font-medium text-warning mb-1">
                        Nicht wiederholen
                      </div>
                      <ul className="text-[12px] text-text-2 italic list-disc list-inside space-y-0.5">
                        {sec.do_not_repeat.map((b, i) => (
                          <li key={i}>{b}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Evidence Notes */}
                  {sec.evidence_notes && sec.evidence_notes.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide font-medium text-text-2 mb-1">
                        Belege
                      </div>
                      <ul className="space-y-1.5">
                        {sec.evidence_notes.map((n, i) => (
                          <li
                            key={i}
                            className="text-[12px] text-text border border-border rounded-md px-2 py-1 bg-surface-2/60"
                          >
                            <div className="flex items-center gap-2 text-[10px] text-text-2 mb-0.5">
                              <span className="font-mono">{n.source || "?"}</span>
                              {n.ref_id && (
                                <span className="font-mono text-text-3">
                                  {n.ref_id}
                                </span>
                              )}
                              {n.use_for_brief_item && (
                                <span className="truncate text-text-3">
                                  &rarr; {n.use_for_brief_item}
                                </span>
                              )}
                            </div>
                            <div>{n.note || "—"}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Format Hints */}
                  {sec.format_hints && sec.format_hints.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide font-medium text-text-2 mb-1">
                        Format-Hinweise
                      </div>
                      <ul className="text-[12px] text-text-2 list-disc list-inside">
                        {sec.format_hints.map((h, i) => (
                          <li key={i}>{h}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Hero Paragraph */}
                  {sec.hero_paragraph && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide font-medium text-violet-700 mb-1">
                        Hero-Absatz (wörtlich in den Text)
                      </div>
                      <div className="text-text leading-relaxed border border-violet-200 bg-violet-50/40 rounded-md p-3 text-[13px]">
                        {sec.hero_paragraph}
                      </div>
                    </div>
                  )}

                  {/* Safety Note */}
                  {sec.safety_note && sec.safety_note.trim().length > 0 && (
                    <div className="text-[12px] text-warning bg-warning-soft border border-warning rounded-md px-2 py-1">
                      <span className="font-medium">Sicherheit: </span>
                      {sec.safety_note}
                    </div>
                  )}

                  {/* RAG Hits (cos_blog_vector) */}
                  {ragHits.length > 0 && (
                    <details className="text-[12px]">
                      <summary className="cursor-pointer text-text-2 hover:text-text">
                        RAG-Hits (cos_blog_vector) - {ragHits.length}
                      </summary>
                      <ul className="mt-2 space-y-2">
                        {ragHits.map((h, i) => (
                          <li
                            key={i}
                            className="border border-border rounded-md px-2 py-1.5 bg-surface-2/40"
                          >
                            <div className="flex items-center gap-2 text-[10px] text-text-2 mb-1 tabular-nums">
                              <span>
                                sim {(h.similarity ?? 0).toFixed(3)}
                              </span>
                              {h.source && (
                                <span className="truncate text-text-3">
                                  {h.source}
                                </span>
                              )}
                            </div>
                            <div className="text-text line-clamp-4">
                              {h.text || "—"}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* FAQ Block (collapsed) */}
      {s.faq_block && s.faq_block.length > 0 && (
        <details className="border border-border rounded-lg bg-surface">
          <summary className="px-4 py-2.5 cursor-pointer text-sm font-medium text-text">
            FAQ Block ({s.faq_block.length})
          </summary>
          <div className="px-4 py-3 border-t border-border space-y-3">
            {s.faq_block.map((f, i) => (
              <div key={i} className="text-sm">
                <div className="font-medium text-text">
                  {f.question || "—"}
                </div>
                <div className="text-text mt-1">{f.answer_brief || "—"}</div>
                {f.differentiation_note && (
                  <div className="text-[11px] text-text-2 italic mt-1">
                    neuer Aspekt: {f.differentiation_note}
                  </div>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Writing Guidelines (collapsed) */}
      {s.writing_guidelines && Object.keys(s.writing_guidelines).length > 0 && (
        <details className="border border-border rounded-lg bg-surface">
          <summary className="px-4 py-2.5 cursor-pointer text-sm font-medium text-text">
            Schreib-Richtlinien
          </summary>
          <pre className="px-4 py-3 border-t border-border text-[11px] text-text whitespace-pre-wrap font-mono bg-surface-2">
            {JSON.stringify(s.writing_guidelines, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

function ImagesTab({ detail }: { detail: ArticleDetail }) {
  if (detail.evidence.length === 0) {
    return (
      <div className="text-sm text-text-2 italic">
        Noch keine Evidence-Blocks. Werden in der Image-Phase nach erfolgreicher
        Generation angelegt.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {detail.evidence.map((ev) => (
        <div
          key={ev.id}
          className="border border-border rounded-lg p-3 bg-surface text-xs"
        >
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-[10px] tracking-wide bg-surface-3 px-1.5 py-0.5 rounded-md">
              {imageRoleLabel(ev.role)}
            </span>
            {ev.used_in_final && (
              <span className="text-[10px] text-success inline-flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> im Artikel
              </span>
            )}
          </div>
          <div className="aspect-video bg-surface-2 rounded-md flex items-center justify-center text-text-3 mb-2 overflow-hidden">
            {ev.image_url ? (
              <a href={ev.image_url} target="_blank" rel="noreferrer">
                <img
                  src={ev.image_url}
                  alt={ev.image_alt ?? ev.role}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </a>
            ) : (
              <ImageIcon className="w-8 h-8" />
            )}
          </div>
          <div className="space-y-1">
            <Row
              label="Quelle"
              value={
                ev.marketing_asset_id
                  ? "statamic-asset"
                  : ev.dam_asset_id
                  ? "dam-drive"
                  : "—"
              }
            />
            {ev.image_generated_by && (
              <Row label="Erzeugt von" value={<code className="text-[10px]">{ev.image_generated_by}</code>} />
            )}
            {ev.image_alt && <Row label="Alt-Text" value={<span className="text-[11px]">{ev.image_alt}</span>} />}
            {ev.image_prompt && (
              <details className="text-[10px] text-text-2">
                <summary className="cursor-pointer">prompt anzeigen</summary>
                <div className="mt-1 p-1 bg-surface-2 rounded-md">{ev.image_prompt}</div>
              </details>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
